import "./styles/tokens.css";
import "./styles/base.css";
import "./styles/layout.css";
import "./styles/cards.css";
import "./styles/map.css";
import "./styles/mobile.css";
import "./styles/notams.css";

import { fetchAirmetSigmet, fetchAirport, fetchFlights, fetchNotams, fetchWeather } from "./api";
import { installBridge } from "./bridge";
import runwaysData from "./data/runways.json";
import { $source, startDiscovery } from "./discover";
import { initMap } from "./map";
import { initSentry } from "./sentry";

// Fire-and-forget — no DSN means no-op, with DSN means a single dynamic
// import of @sentry/browser. Doesn't block first paint.
void initSentry();

import { extractAirports, nearestAirport } from "./nearest-airport";
import {
  $airmet,
  $airport,
  $currentIcao,
  $flights,
  $icaoUserPicked,
  $notams,
  $station,
  $weather,
} from "./store";
import { renderAirportPanel } from "./ui/airport";
import { renderDetail } from "./ui/detail";
import { renderHeader } from "./ui/header";
import { renderLegend } from "./ui/legend";
import { renderMobile } from "./ui/mobile";
import { renderNotamPanel } from "./ui/notams";
import { renderRealtime } from "./ui/realtime";

const app = document.getElementById("app")!;
app.innerHTML = `
  <div id="map" class="map-base"></div>
  <div class="map-tint"></div>
  <div class="stage" id="stage">
    <div id="header"></div>
    <div class="left-rail">
      <div id="detail"></div>
      <div id="realtime"></div>
    </div>
    <div class="right-rail" id="rightRail"></div>
    <div id="legend"></div>
    <div id="notamPanelHost"></div>
  </div>
  <div id="mobileRoot"></div>
`;

initMap("map");
installBridge();

renderHeader(document.getElementById("header")!);
renderDetail(document.getElementById("detail")!);
renderRealtime(document.getElementById("realtime")!);
renderAirportPanel(document.getElementById("rightRail")!);
renderLegend(document.getElementById("legend")!);
renderMobile(document.getElementById("mobileRoot")!);
renderNotamPanel(document.getElementById("notamPanelHost")!);

// Mount the same Web detail card + airport panel into the mobile bottom
// sheet slots that renderMobile created. Each renderer subscribes to its
// own atoms, so both the desktop and mobile copies stay in sync without
// extra plumbing. The `?` guards let renderMobile defer DOM creation
// safely on viewports where mobileRoot isn't laid out yet.
const mDetailHost = document.getElementById("mDetailHost");
if (mDetailHost) renderDetail(mDetailHost);
const mAirportHost = document.getElementById("mAirportPanelHost");
if (mAirportHost) renderAirportPanel(mAirportHost);

// Kick off discovery (sets $source, drives bridge below).
startDiscovery();

// Hydrate server-side data for the currently-focused airport. Re-runs on
// data-source changes (LAN feeder discovered mid-session) AND when the user
// clicks a different airport pin.
async function hydrate() {
  const icao = $currentIcao.get();
  const [flights, airport, weather, notams, airmet] = await Promise.all([
    fetchFlights(icao),
    fetchAirport(icao),
    fetchWeather(icao),
    fetchNotams(icao),
    fetchAirmetSigmet(),
  ]);
  // Discard out-of-order responses: user may have clicked a different
  // airport while these fetches were inflight.
  if ($currentIcao.get() !== icao) return;
  $flights.set(flights);
  if (airport) $airport.set(airport);
  if (weather) $weather.set(weather);
  // Body-less NOTAMs (only validity window + Q-code) add noise to both the
  // badge count and the panel — drop them at the store boundary so every
  // consumer sees the same filtered list.
  $notams.set(notams.filter((n) => n.body?.trim()));
  if (airmet) $airmet.set(airmet);
}
hydrate();
$source.subscribe(() => hydrate());
$currentIcao.subscribe(() => hydrate());

// Until the user clicks an airport pin themselves, follow station moves:
// every time $station resolves to a real (non-default) location, retarget
// $currentIcao to the nearest airport in runways.json so the right rail
// shows flights/weather/NOTAMs for *the airport the user actually feeds*.
const knownAirports = extractAirports(runwaysData as Record<string, unknown>);
$station.subscribe((s) => {
  if ($icaoUserPicked.get()) return;
  // The Setagaya fallback is not a real fix — retargeting from it would
  // flip the default RJTT to whatever is nearest of the (now 108-strong)
  // airport list (RJTF). Only follow real station locations.
  if (s.source === "default") return;
  const nearest = nearestAirport({ lat: s.lat, lon: s.lon }, knownAirports);
  if (nearest && nearest.icao !== $currentIcao.get()) $currentIcao.set(nearest.icao);
});

// Periodic refresh — METAR every 5 min, AIRMET every 5 min, NOTAMs every 2 min.
// Always re-reads $currentIcao so airport switches see fresh data.
//
// guarded() wraps a periodic fetch with a per-call inflight flag so a
// slow API doesn't stack up parallel requests. Without this, a 30 s
// API hiccup turns a 5-min interval into a queue of 6 simultaneous
// requests when the API recovers — bad for both client memory and
// the API's per-IP rate limit. See SCALING_AUDIT.md S-07.
function guarded(fn: () => Promise<unknown>): () => void {
  let inflight = false;
  return () => {
    if (inflight) return;
    inflight = true;
    fn().finally(() => {
      inflight = false;
    });
  };
}
setInterval(
  guarded(() => fetchWeather($currentIcao.get()).then((w) => w && $weather.set(w))),
  5 * 60_000,
);
setInterval(
  guarded(() => fetchAirmetSigmet().then((a) => a && $airmet.set(a))),
  5 * 60_000,
);
setInterval(
  guarded(() => fetchNotams($currentIcao.get()).then((n) => $notams.set(n.filter((r) => r.body?.trim())))),
  2 * 60_000,
);
